import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Target, Briefcase, CalendarDays, Map, Warehouse, MessageSquare, Clock,
  ClipboardCheck, FileText, Settings, Lightbulb, ChevronDown, ChevronRight,
  Zap, Shield, BarChart3, DollarSign, Users, AlertTriangle, ShoppingCart,
  Truck, Boxes, FileCheck, Award, Megaphone, UserCheck, Wrench, Receipt,
  Bell, Database, ExternalLink, UserPlus, ClipboardList, FileWarning,
  Layers, ShieldCheck, TrendingUp,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useRole } from "@/data/RoleContext";
import { useBranding } from "@/data/BrandingContext";
import { useNavMode } from "@/data/NavModeContext";
import { isRouteAllowed } from "@/data/rolePermissions";
import { isRouteFoldedForRole } from "@/data/foldRegistry";
import { useSidebarMode } from "@/data/sidebarMode";
import {
  isSystemDrawerRoute,
  isDrawerOpenByDefault,
  getStoredDrawerOpen,
  setStoredDrawerOpen,
} from "@/data/systemDrawer";
import { useAppData } from "@/data/AppDataContext";
import { getOpenHandoffCountForViewer } from "@/data/handoffRouting";
import { TenantSwitcher } from "@/components/TenantSwitcher";
import { Button } from "@/components/ui/button";
import { QuickActionsModal } from "@/components/quick-actions/QuickActionsModal";

interface RailItem {
  label: string;
  path: string;
  icon: React.ElementType;
}

interface RailGroup {
  label: string;
  items: RailItem[];
}

const HIDDEN_LEFT_NAV_PATHS = new Set(["/execution/installers", "/contractor-intake"]);

function isHiddenLeftNavItem(item: Pick<RailItem, "path">): boolean {
  return HIDDEN_LEFT_NAV_PATHS.has(item.path);
}

/* ─────────────────────────────────────────────
   Curated Command Rail — primary surfaces
   ───────────────────────────────────────────── */

const PRIMARY: RailItem[] = [
  { label: "Leadership Dashboard", path: "/command-dashboard", icon: Target },
  { label: "Jobs", path: "/jobs", icon: Briefcase },
];

const RAIL_GROUPS: RailGroup[] = [
  {
    label: "Operations",
    items: [
      { label: "Scheduling", path: "/scheduling", icon: CalendarDays },
      { label: "Dispatch Board", path: "/scheduling-dispatch", icon: Map },
      { label: "Warehouse Hub", path: "/warehouse", icon: Warehouse },
    ],
  },
  {
    label: "Sales",
    items: [
      { label: "Leads", path: "/pipeline", icon: Target },
      { label: "Opportunities", path: "/opportunities", icon: Target },
      { label: "Follow-Ups", path: "/follow-ups", icon: Clock },
      { label: "Appointments", path: "/appointments", icon: CalendarDays },
    ],
  },
  {
    label: "Pre-Construction",
    items: [
      { label: "Build Review", path: "/intake-review", icon: ClipboardCheck },
      { label: "Permit Tracker", path: "/permitting", icon: FileText },
    ],
  },
];

const TERTIARY: RailItem[] = [
  { label: "Settings", path: "/settings", icon: Settings },
  { label: "Demo Lab", path: "/demo-lab", icon: Lightbulb },
  { label: "Friction Log", path: "/friction-log", icon: MessageSquare },
];

/* ─────────────────────────────────────────────
   "More" — every route that isn't above, so
   nothing becomes unreachable.
   ───────────────────────────────────────────── */

const MORE_CATALOG: RailItem[] = [
  { label: "Customers", path: "/customers", icon: UserCheck },
  { label: "Contractors", path: "/contractors", icon: Users },
  // Conversations hidden — surfaced inline on Customer Profile (route preserved)
  { label: "Issues", path: "/issues", icon: AlertTriangle },
  { label: "Service Tickets", path: "/service-tickets", icon: Wrench },
  { label: "Install QA", path: "/qa-form", icon: ClipboardCheck },
  { label: "Report Issue", path: "/issue-form", icon: FileWarning },
  { label: "Project Closeout", path: "/closeout", icon: FileCheck },
  { label: "Install Readiness", path: "/install-readiness", icon: ShieldCheck },
  { label: "Installer Dashboard", path: "/installer-dashboard", icon: Wrench },
  { label: "Dispatch Intelligence", path: "/dispatch-intelligence", icon: Zap },
  { label: "Ordering Queue", path: "/ordering-queue", icon: ShoppingCart },
  { label: "Vendors", path: "/vendors", icon: Truck },
  { label: "Inventory", path: "/inventory", icon: Boxes },
  { label: "Appointment Results", path: "/appointment-results", icon: FileCheck },
  { label: "Rep Scorecards", path: "/rep-scorecards", icon: Award },
  { label: "Lead Performance", path: "/growth-channels", icon: Megaphone },
  { label: "Lead Sources", path: "/lead-sources", icon: Megaphone },
  { label: "Lead Integrity", path: "/lead-integrity", icon: Shield },
  { label: "Marketing Attribution", path: "/marketing-attribution", icon: BarChart3 },
  { label: "Reports", path: "/reports", icon: BarChart3 },
  { label: "Disposition Report", path: "/disposition-report", icon: FileText },
  { label: "Alerts History", path: "/alerts", icon: Bell },
  { label: "System Audit", path: "/system-audit", icon: Shield },
  { label: "Architecture Health", path: "/architecture-health", icon: Shield },
  { label: "Intelligence", path: "/intelligence", icon: Lightbulb },
  { label: "Pipeline Intelligence", path: "/pipeline-intelligence", icon: Target },
  { label: "Automations", path: "/automations", icon: Zap },
  { label: "Follow-Up Engine", path: "/follow-up-engine", icon: Zap },
  { label: "Accounting", path: "/accounting", icon: DollarSign },
  { label: "Revenue Intelligence", path: "/revenue-intelligence", icon: TrendingUp },
  { label: "Earnings Intelligence", path: "/earnings-intelligence", icon: TrendingUp },
  { label: "Commissions", path: "/commissions", icon: Receipt },
  { label: "Contractor Intake", path: "/contractor-intake", icon: ClipboardList },
  { label: "Contractor Onboarding", path: "/contractor-onboarding", icon: UserPlus },
  { label: "Portal Hub", path: "/portals", icon: ExternalLink },
  { label: "Offerings", path: "/offerings", icon: Layers },
  { label: "Product Registry", path: "/product-registry", icon: Database },
  { label: "Control Plane", path: "/control-plane", icon: Shield },
  { label: "Navigation Settings", path: "/navigation-settings", icon: Settings },
  { label: "Migration Review", path: "/import/review-queue", icon: ClipboardList },
  { label: "Lead / Appointment Import", path: "/import/leads-appointments", icon: CalendarDays },
];

/* ─────────────────────────────────────────────
   COMPONENT
   ───────────────────────────────────────────── */

export function CommandRailSidebar() {
  const location = useLocation();
  const { role, roleInfo } = useRole();
  const { branding } = useBranding();
  const { setNavMode } = useNavMode();
  const { followUpTasks } = useAppData();
  const [moreOpen, setMoreOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const { mode: sidebarMode } = useSidebarMode(role);
  const storedDrawerOpen = getStoredDrawerOpen();
  const [systemOpen, setSystemOpen] = useState<boolean>(
    storedDrawerOpen !== null ? storedDrawerOpen : isDrawerOpenByDefault(role),
  );
  const toggleSystemDrawer = () => {
    setSystemOpen((prev) => {
      const next = !prev;
      setStoredDrawerOpen(next);
      return next;
    });
  };

  // Hand-off count for the Command Center sidebar badge — same resolver
  // the inbox uses, so the number can never drift from what's visible.
  const handoffCount = useMemo(
    () => getOpenHandoffCountForViewer({ role, label: roleInfo.label }, followUpTasks),
    [role, roleInfo.label, followUpTasks],
  );

  const isActive = (path: string) => {
    if (location.pathname === path) return true;
    if (path === "/") return false;
    // Prefix match only when the next char is "/" — prevents /scheduling matching /scheduling-dispatch.
    return location.pathname.startsWith(path + "/");
  };

  // Filter helpers — respect ROLE_ALLOWED_ROUTES and fold registry.
  // Routes folded for this role are hidden from the sidebar but remain live.
  // System routes are removed here and rendered inside the System drawer.
  const filterItems = (items: RailItem[]) =>
    items.filter(
      (i) => isRouteAllowed(role, i.path) &&
        (sidebarMode === "legacy" || !isRouteFoldedForRole(i.path, role)) &&
        !isHiddenLeftNavItem(i) &&
        !isSystemDrawerRoute(i.path),
    );

  const visiblePrimary = useMemo(() => filterItems(PRIMARY), [role, sidebarMode]);
  const visibleGroups = useMemo(
    () =>
      RAIL_GROUPS.map((g) => ({ ...g, items: filterItems(g.items) })).filter(
        (g) => g.items.length > 0
      ),
    [role, sidebarMode]
  );
  const visibleTertiary = useMemo(() => filterItems(TERTIARY), [role, sidebarMode]);

  // "More" = curated catalog minus anything already shown above and minus System routes.
  const shownPaths = useMemo(() => {
    const set = new Set<string>();
    visiblePrimary.forEach((i) => set.add(i.path));
    visibleGroups.forEach((g) => g.items.forEach((i) => set.add(i.path)));
    visibleTertiary.forEach((i) => set.add(i.path));
    return set;
  }, [visiblePrimary, visibleGroups, visibleTertiary]);

  const moreItems = useMemo(
    () =>
      MORE_CATALOG.filter(
        (i) =>
          !shownPaths.has(i.path) &&
          isRouteAllowed(role, i.path) &&
          (sidebarMode === "legacy" || !isRouteFoldedForRole(i.path, role)) &&
          !isHiddenLeftNavItem(i) &&
          !isSystemDrawerRoute(i.path),
      ),
    [shownPaths, role, sidebarMode]
  );

  // System drawer items — every System route this role can access,
  // sourced from the same MORE_CATALOG + TERTIARY pool so labels/icons stay consistent.
  const systemItems = useMemo(() => {
    const pool: RailItem[] = [...TERTIARY, ...MORE_CATALOG];
    const seen = new Set<string>();
    return pool.filter((i) => {
      if (!isSystemDrawerRoute(i.path)) return false;
      if (!isRouteAllowed(role, i.path)) return false;
      if (seen.has(i.path)) return false;
      seen.add(i.path);
      return true;
    });
  }, [role]);


  return (
    <aside className="hidden md:flex w-[232px] flex-col bg-sidebar border-r border-sidebar-border shrink-0 sticky top-0 h-screen">
      {/* Brand */}
      <div className="px-5 py-4 border-b border-sidebar-border">
        <div className="flex items-center gap-2.5">
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={branding.companyName} className="h-7 w-7 rounded-lg object-contain" />
          ) : (
            <div className="h-7 w-7 rounded-lg bg-sidebar-primary flex items-center justify-center">
              <Shield className="h-3.5 w-3.5 text-sidebar-primary-foreground" />
            </div>
          )}
          <div className="flex items-baseline gap-1.5">
            <h1 className="text-[15px] font-bold text-sidebar-accent-foreground tracking-tight leading-none">
              {branding.platformName}
            </h1>
            <span className="text-[10px] font-semibold text-sidebar-primary tracking-[0.08em] leading-none px-1.5 py-0.5 rounded-sm bg-sidebar-primary/12 border border-sidebar-primary/25">
              OS v0.6
            </span>
          </div>
        </div>
      </div>

      {/* Tenant */}
      <div className="border-b border-sidebar-border">
        <TenantSwitcher />
      </div>

      {/* Quick Action */}
      <div className="px-3 pt-3">
        <Button
          onClick={() => setQuickOpen(true)}
          className="w-full justify-start gap-2 h-9 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
        >
          <Zap className="h-4 w-4" />
          <span className="text-sm font-semibold">Quick Action</span>
        </Button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-3 overflow-y-auto scrollbar-thin">
        {/* PRIMARY — strongest emphasis */}
        <div className="space-y-1 mb-5">
          {visiblePrimary.map((item) => {
            const active = isActive(item.path);
            const isCommandCenter = item.path === "/command-dashboard";
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex items-center gap-2.5 pl-3 pr-2.5 py-2 rounded-md text-[14px] font-semibold transition-all border-l-[3px]",
                  active
                    ? "bg-sidebar-accent text-sidebar-accent-foreground border-sidebar-primary shadow-sm"
                    : "text-sidebar-foreground/85 border-transparent hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
                )}
              >
                <item.icon className={cn("h-4 w-4 shrink-0", active && "text-sidebar-primary")} />
                <span className="flex-1">{item.label}</span>
                {isCommandCenter && handoffCount > 0 && (
                  <span
                    className="ml-auto inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full bg-warning text-warning-foreground text-[10px] font-bold leading-none"
                    title={`${handoffCount} open hand-off${handoffCount === 1 ? "" : "s"}`}
                  >
                    {handoffCount}
                  </span>
                )}
              </Link>
            );
          })}
        </div>

        {/* SECONDARY GROUPS */}
        {visibleGroups.map((group) => (
          <div key={group.label} className="mb-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-sidebar-foreground/40 px-2 mb-1.5">
              {group.label}
            </p>
            <div className="space-y-px">
              {group.items.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-2.5 pl-3 pr-2.5 py-[7px] rounded-md text-[13px] font-medium transition-all border-l-2",
                      active
                        ? "bg-sidebar-accent text-sidebar-accent-foreground border-sidebar-primary"
                        : "text-sidebar-foreground/65 border-transparent hover:bg-sidebar-accent/40 hover:text-sidebar-foreground"
                    )}
                  >
                    <item.icon className={cn("h-3.5 w-3.5 shrink-0", active && "text-sidebar-primary")} />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        {/* TERTIARY — subtle */}
        {visibleTertiary.length > 0 && (
          <div className="mb-3 pt-3 border-t border-sidebar-border/60">
            <div className="space-y-px">
              {visibleTertiary.map((item) => {
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      "flex items-center gap-2.5 pl-3 pr-2.5 py-[6px] rounded-md text-[12px] font-medium transition-all",
                      active
                        ? "bg-sidebar-accent/70 text-sidebar-accent-foreground"
                        : "text-sidebar-foreground/45 hover:bg-sidebar-accent/30 hover:text-sidebar-foreground/80"
                    )}
                  >
                    <item.icon className="h-3.5 w-3.5 shrink-0" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* MORE — collapsed by default, ensures all routes reachable */}
        {moreItems.length > 0 && (
          <div className="pt-3 border-t border-sidebar-border/60">
            <button
              onClick={() => setMoreOpen((v) => !v)}
              className="w-full flex items-center justify-between px-2 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.16em] text-sidebar-foreground/55 hover:text-sidebar-foreground hover:bg-sidebar-accent/30 transition-colors"
              aria-expanded={moreOpen}
            >
              <span>More ({moreItems.length})</span>
              {moreOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </button>
            {moreOpen && (
              <div className="mt-1 space-y-px">
                {moreItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(
                        "flex items-center gap-2.5 pl-3 pr-2.5 py-[6px] rounded-md text-[12px] font-medium transition-all",
                        active
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground/55 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground"
                      )}
                    >
                      <item.icon className="h-3.5 w-3.5 shrink-0" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* SYSTEM DRAWER (Phase 3) — admin/config/governance routes */}
        {systemItems.length > 0 && (
          <div className="pt-3 border-t border-sidebar-border/60">
            <button
              onClick={toggleSystemDrawer}
              className="w-full flex items-center justify-between px-2 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.16em] text-sidebar-foreground/55 hover:text-sidebar-foreground hover:bg-sidebar-accent/30 transition-colors"
              aria-expanded={systemOpen}
            >
              <span className="flex items-center gap-1.5">
                <Settings className="h-3 w-3" />
                System
                <span className="text-sidebar-foreground/35 normal-case tracking-normal font-semibold">
                  ({systemItems.length})
                </span>
              </span>
              {systemOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </button>
            {systemOpen && (
              <div className="mt-1 space-y-px">
                {systemItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(
                        "flex items-center gap-2.5 pl-3 pr-2.5 py-[6px] rounded-md text-[12px] font-medium transition-all",
                        active
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "text-sidebar-foreground/55 hover:bg-sidebar-accent/40 hover:text-sidebar-foreground"
                      )}
                    >
                      <item.icon className="h-3.5 w-3.5 shrink-0" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Footer — mode switcher */}
      <div className="px-3 py-2.5 border-t border-sidebar-border space-y-1.5">
        <button
          onClick={() => setNavMode("standard")}
          className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-[11px] font-medium text-sidebar-foreground/55 hover:text-sidebar-foreground hover:bg-sidebar-accent/40 transition-colors"
          title="Switch to Standard Sidebar"
        >
          <span className="uppercase tracking-[0.12em]">Command Rail</span>
          <span className="text-sidebar-foreground/40">switch →</span>
        </button>
        <p className="text-[9px] text-sidebar-foreground/20 font-medium px-2.5">
          {branding.platformName} OS · v0.6-beta
        </p>
      </div>

      <QuickActionsModal open={quickOpen} onOpenChange={setQuickOpen} />
    </aside>
  );
}
